import { useEffect, useRef } from 'react';
import { Box } from '@chakra-ui/react';

const HUBSPOT_SCRIPT_URL = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
const HUBSPOT_SCRIPT_ID = 'hubspot-meetings-embed-script';

interface HubSpotMeetingProps {
  meetingUrl: string;
  onMeetingBooked?: (payload: unknown) => void;
}

export function HubSpotMeeting({ meetingUrl, onMeetingBooked }: HubSpotMeetingProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const existingScript = document.getElementById(HUBSPOT_SCRIPT_ID);
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = HUBSPOT_SCRIPT_ID;
      script.type = 'text/javascript';
      script.src = HUBSPOT_SCRIPT_URL;
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const hideScrollbars = () => {
      const iframe = container.querySelector('iframe');
      if (iframe) {
        iframe.style.overflow = 'hidden';
        iframe.setAttribute('scrolling', 'no');
      }
    };

    const observer = new MutationObserver(hideScrollbars);
    observer.observe(container, {
      childList: true,
      subtree: true,
    });

    hideScrollbars();

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!onMeetingBooked) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.includes('hubspot.com')) {
        return;
      }

      const data = event.data;

      const isBookingEvent =
        data &&
        typeof data === 'object' &&
        (data.eventName === 'meetingBooked' ||
          data.type === 'hs-meetings-booking' ||
          data.hubspot_meeting === true ||
          data.event === 'meetingBooked' ||
          data.eventName === 'meetingScheduled' ||
          data.meetingBookSucceeded === true ||
          data.meetingBookSucceeded === 'true');

      if (isBookingEvent) {
        onMeetingBooked(data);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onMeetingBooked]);

  const embedUrl = meetingUrl.includes('embed=true')
    ? meetingUrl
    : meetingUrl.includes('?')
      ? `${meetingUrl}&embed=true`
      : `${meetingUrl}?embed=true`;

  return (
    <Box width="100%" height="780px">
      <div
        ref={containerRef}
        className="meetings-iframe-container"
        data-src={embedUrl}
        style={{ width: '100%', height: '100%' }}
      />
    </Box>
  );
}
